import React, { useState, useRef, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Edit2, Check, X } from 'lucide-react';

interface EditableTextFieldProps {
    value: string;
    onSave: (newValue: string) => Promise<void>;
    placeholder?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    displayStyle?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    showEditButton?: boolean;
    multiline?: boolean;
    rows?: number;
}

export const EditableTextField: React.FC<EditableTextFieldProps> = ({
    value,
    onSave,
    placeholder = 'Ange värde',
    label,
    required = false,
    disabled = false,
    displayStyle = {},
    inputStyle = {},
    showEditButton = true,
    multiline = false,
    rows = 3,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    // Update local state when value prop changes
    useEffect(() => {
        setEditValue(value);
    }, [value]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleSave = async () => {
        if (required && !editValue.trim()) return;
        if (editValue === value) {
            setIsEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(editValue);
            setIsEditing(false);
        } catch (err) {
            // Error handling is done in the parent component
            console.error('Error saving:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    if (isEditing) {
        return (
            <div className="editable-text-field editing">
                {label && <div className="editable-text-label">{label}</div>}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    {multiline ? (
                        <Form.Control
                            as="textarea"
                            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            disabled={saving}
                            rows={rows}
                            style={{ flex: 1, ...inputStyle }}
                        />
                    ) : (
                        <Form.Control
                            type="text"
                            ref={inputRef as React.RefObject<HTMLInputElement>}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            disabled={saving}
                            style={{ flex: 1, ...inputStyle }}
                        />
                    )}
                    <Button
                        size="sm"
                        variant="success"
                        onClick={handleSave}
                        disabled={saving || (required && !editValue.trim())}
                    >
                        <Check size={16} />
                    </Button>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        <X size={16} />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="editable-text-field display">
            {label && (
                <div className="editable-text-label" style={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    {label}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', ...displayStyle }}>
                    {value || <span style={{ color: '#999', fontStyle: 'italic' }}>{placeholder}</span>}
                </span>
                {showEditButton && !disabled && (
                    <Button
                        size="sm"
                        variant="link"
                        onClick={() => setIsEditing(true)}
                        style={{ padding: '2px 6px', color: '#666' }}
                    >
                        <Edit2 size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
};
